import type { Checksum, Checksummable } from '../core/Checksum';
import type { ControlInput } from './FlightState';

/**
 * The mission-start control hold.
 *
 * For the first few seconds of a mission the aircraft flies itself: the stick is ignored and the
 * throttle is pinned to the spawn trim. The problem it solves is not hypothetical — the player
 * arrives from a briefing screen, and a key still down from it, or an impatient press during the
 * world build, moves the aircraft before anyone is looking at the picture. A one-way drone has no
 * way to undo the first two seconds of a mission.
 *
 * The world is *not* frozen. The aircraft flies trimmed and level, the mission clock runs and the
 * threats look for it, so nothing downstream of here needs a special case for the hold.
 *
 * Time comes from the fixed timestep only, never from a wall clock, so the hold lasts the same
 * number of ticks in a replay as it did in the flight (CLAUDE.md: the simulation is deterministic).
 */
export class ControlLock implements Checksummable {
  private remaining = 0;

  /** Start a hold. `seconds` of 0 or less leaves the controls live. */
  reset(seconds: number): void {
    this.remaining = Math.max(0, seconds);
  }

  /** True while the controls are held. */
  get active(): boolean {
    return this.remaining > 0;
  }

  /** Seconds left on the hold, 0 once the controls are live. */
  get remainingS(): number {
    return this.remaining;
  }

  /** Advance the hold. `dtS` is the fixed timestep, seconds. */
  tick(dtS: number): void {
    if (this.remaining <= 0) return;
    this.remaining = Math.max(0, this.remaining - dtS);
  }

  /**
   * Neutralise a command while the hold is on. `trimThrottle` is the spawn setting, 0..1.
   *
   * Throttle is *held* rather than zeroed: the aircraft is trimmed for level flight at spawn, and
   * cutting the engine for five seconds would hand the player exactly the sinking aircraft the
   * trimmed spawn exists to avoid.
   */
  apply(control: ControlInput, trimThrottle: number): void {
    if (!this.active) return;
    control.rollCmd = 0;
    control.pitchCmd = 0;
    control.yawCmd = 0;
    control.throttle = trimThrottle;
  }

  checksum(into: Checksum): void {
    into.addFloat(this.remaining);
  }
}
